import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  Signal,
  signal,
  WritableSignal,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { interval, Observable, startWith, Subject, switchMap, takeUntil } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { FilteredAuctionTimerComponent } from '../../components/filtered-auction-timer/filtered-auction-timer.component';
import { AuctionsService } from '../../services/auctions.service';
import { Auction } from '../../models/auction.model';
import { CategoryFilterComponent } from '../../components/category-filter/category-filter.component';
import { Category } from '../../models/category.model';
import { toSignal } from '@angular/core/rxjs-interop';
import { ScrollingModule } from '@angular/cdk/scrolling';

@Component({
  selector: 'app-auctions-list-page',
  standalone: true,
  imports: [CommonModule, ButtonModule, FilteredAuctionTimerComponent,CategoryFilterComponent, ScrollingModule],
  templateUrl: './auctions-list-page.component.html',
  styleUrls: ['./auctions-list-page.component.scss'],
})
export class AuctionsListPageComponent implements OnInit, OnDestroy {
  open = signal(false);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private auctionsService = inject(AuctionsService);
  private destroy$ = new Subject<void>();

  public auctions: WritableSignal<Auction[]> = signal<Auction[]>([]);

  protected now = signal<number>(Date.now());

  private scheduledStarts = new Map<string, number>();

   protected categories: Signal<Category[]> = toSignal(
    this.pollEvery(30000, () => this.auctionsService.getCategoriesWithCount()),
    { initialValue: [] }
  );
page = signal(1);
loading = signal(false);
endReached = signal(false);

  ngOnInit(): void {
    const categoryId = this.route.snapshot.paramMap.get('id');
this.loadInitial();
    interval(1000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.now.set(Date.now());
        this.updateAuctionStates();
      });
  }
private loadInitial() {
  this.page.set(1);
  this.endReached.set(false);
  this.auctions.set([]);

  const categoryId = this.route.snapshot.paramMap.get('id');
  this.fetchPage(categoryId, 1);
}

private fetchPage(categoryId: string | null, page: number) {
  if (this.loading() || this.endReached()) return;

  this.loading.set(true);

  const request$ = categoryId
    ? this.auctionsService.getAuctionsListByCategory(categoryId, page)
    : this.auctionsService.getAuctionsList(page);

  request$.pipe(takeUntil(this.destroy$)).subscribe({
    next: (data) => {
      if (!data || data.length === 0) {
        this.endReached.set(true);
        this.loading.set(false);
        return;
      }

      this.auctions.update((curr) => [...curr, ...data]);

      this.rebuildScheduledIndex([...this.auctions()]);

      this.page.update((p) => p + 1);
      this.loading.set(false);
    },
    error: () => {
      this.loading.set(false);
      this.endReached.set(true);
    },
  });
}
public onScroll(event: any) {
  const target = event.target;

  const scrollPosition = target.scrollTop + target.clientHeight;
  const scrollThreshold = target.scrollHeight - 200; 

  if (scrollPosition >= scrollThreshold) {
    const categoryId = this.route.snapshot.paramMap.get('id');
    this.fetchPage(categoryId, this.page());
  }
}

  private rebuildScheduledIndex(list: Auction[]): void {
    this.scheduledStarts.clear();
    for (const a of list) {
      if (a?.state === 'scheduled' && a.startAt) {
        const startMs = Date.parse(a.startAt);
        if (!isNaN(startMs)) this.scheduledStarts.set(a.id, startMs);
      }
    }
  }

  private updateAuctionInList(
    id: string,
    updater: (a: Auction) => Auction
  ): void {
    this.auctions.update((current) => {
      const idx = current.findIndex((it) => it.id === id);
      if (idx === -1) return current;
      const copy = current.slice();
      copy[idx] = updater(copy[idx]);
      return copy;
    });
  }

private updateAuctionStates(): void {
  if (this.scheduledStarts.size === 0 && this.auctions().length === 0) return;

  const nowMs = this.now();

  const toActivate: string[] = [];
  for (const [id, startMs] of this.scheduledStarts) {
    if (nowMs >= startMs) toActivate.push(id);
  }

  if (toActivate.length > 0) {
    for (const id of toActivate) {
      this.updateAuctionInList(id, (a) => {
        const newA: Auction = { ...a, state: 'ongoing' };

        if (typeof a.duration === 'number' && a.duration > 0) {
          newA.timeLeft = a.duration;
          const computedEndMs = nowMs + a.duration * 1000;
          newA.endAt = new Date(computedEndMs).toISOString();
        } else if (a.endAt) {
          const endMs = Date.parse(a.endAt);
          newA.timeLeft = !isNaN(endMs) ? Math.max(0, Math.floor((endMs - nowMs) / 1000)) : (a.timeLeft ?? 0);
        } else {
          newA.timeLeft = a.timeLeft ?? 0;
        }

        return newA;
      });

      this.scheduledStarts.delete(id);
    }
  }

  const current = this.auctions();
  const toRemove: string[] = [];

  for (const a of current) {
    if (!a || a.state !== 'ongoing') continue;

    let remainingSeconds: number | null = null;

    if (a.endAt) {
      const endMs = Date.parse(a.endAt);
      if (!isNaN(endMs)) {
        remainingSeconds = Math.max(0, Math.floor((endMs - nowMs) / 1000));
      }
    }

    if (remainingSeconds === null && typeof a.duration === 'number' && a.duration > 0 && a.startAt) {
      const startMs = Date.parse(a.startAt);
      if (!isNaN(startMs)) {
        remainingSeconds = Math.max(0, Math.floor((startMs + a.duration * 1000 - nowMs) / 1000));
      }
    }

    if (remainingSeconds === null && typeof a.timeLeft === 'number') {
      remainingSeconds = Math.max(0, Math.floor(a.timeLeft) - 1);
    }

    if (remainingSeconds === null) continue;

    if (remainingSeconds <= 0) {
      toRemove.push(a.id);
    } else {
      this.updateAuctionInList(a.id, (prev) => ({ ...prev, timeLeft: remainingSeconds }));
    }
  }

  if (toRemove.length > 0) {
    this.auctions.update((list) => list.filter((a) => !toRemove.includes(a.id)));
    for (const id of toRemove) {
      this.scheduledStarts.delete(id);
    }
  }
}


  public goToAuction(auction: Auction): void {
    if (auction?.state === 'ongoing') {
      this.router.navigate(['/auction', auction.id]);
    }
  }

  private secondsUntilStart(auction: Auction): number {
    if (!auction?.startAt) return 0;
    const startMs = Date.parse(auction.startAt);
    const diffMs = startMs - this.now();
    return Math.max(0, Math.floor(diffMs / 1000));
  }

  private buildTimeUnits(totalSeconds: number) {
    const pad = (n: number) => (n < 10 ? '0' + n : '' + n);

    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const rawUnits = {
      days: {
        label: this.pluralize(days, ['dzień', 'dni', 'dni']),
        value: pad(days),
        raw: days,
      },
      hours: {
        label: this.pluralize(hours, ['godzina', 'godziny', 'godzin']),
        value: pad(hours),
        raw: hours,
      },
      minutes: {
        label: this.pluralize(minutes, ['minuta', 'minuty', 'minut']),
        value: pad(minutes),
        raw: minutes,
      },
      seconds: {
        label: this.pluralize(seconds, ['sekunda', 'sekundy', 'sekund']),
        value: pad(seconds),
        raw: seconds,
      },
    };

    if (totalSeconds >= 86400) {
      return [rawUnits.days, rawUnits.hours];
    }

    if (totalSeconds >= 3600) {
      return [rawUnits.hours, rawUnits.minutes];
    }

    return [rawUnits.minutes, rawUnits.seconds];
  }

  public timeUnitsFor(auction: Auction) {
    const secs = this.secondsUntilStart(auction);
    return this.buildTimeUnits(secs);
  }

  private pluralize(value: number, forms: [string, string, string]): string {
    if (value === 1) return forms[0];
    if (
      value % 10 >= 2 &&
      value % 10 <= 4 &&
      (value % 100 < 10 || value % 100 >= 20)
    ) {
      return forms[1];
    }
    return forms[2];
  }

  filterDataForFocusedView() {
     return  this.categories()
}
   

    private pollEvery<T>(ms: number, getFn: () => Observable<T>) {
      return interval(ms).pipe(
        startWith(0),
        switchMap(() => getFn()),
        takeUntil(this.destroy$)
      );
    }


  public filterAuctions(categoryId: string): void {
    // console.log('filterAuctions', categoryId);

    // const viewFromQuery = this.route.snapshot.queryParamMap.get('view');
    // const isCodes =
    //   this.homePageService.focusedView$() === 'codes' ||
    //   viewFromQuery === 'codes' ||
    //   window.location.pathname.includes('/codes');
    // console.log('isCodes', isCodes);
    // if (isCodes) {
    //   this.auctionsService
    //     .getCodesListByCategory(categoryId)
    //     .pipe(takeUntil(this.destroy$))
    //     .subscribe({
    //       next: (codesList: any) => {
    //         console.log('codes-reel', codesList);
    //         this.router.navigate(['/codes-reel/', categoryId], {
    //           state: { codesList },
    //         });
    //       },
    //       error: (error) => {
    //         console.error('getCodesListByCategory failed', error);
    //       },
    //     });

    //   return;
    // }

    this.auctionsService.getAuctionsListByCategory(categoryId).subscribe({
      next: (auctions) => {
        this.router.navigate(['/auction-list/', categoryId], {
          state: { auctions },
        });
      },
      error: (error) => {
        console.error('getAuctionsListByCategory failed', error);
      },
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
